import type { PublicUser } from "@codvero/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import QRCode from "qrcode";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useConfirm } from "@/ui/ConfirmDialog.tsx";

import { authApi } from "@/api/auth.ts";
import { authStatusQuery } from "@/features/auth/session.ts";
import { Alert, Button, Card, CodeInput, Field, Input } from "@/ui/primitives.tsx";
import { useApiError } from "@/ui/useApiError.ts";

export function SecuritySettings({ user }: { user: PublicUser }) {
  return (
    <div className="space-y-6">
      <PasswordCard />
      <TotpCard user={user} />
      <SessionsCard />
    </div>
  );
}

function PasswordCard() {
  const { t } = useTranslation();
  const toMsg = useApiError();
  const [form, setForm] = useState({ currentPassword: "", newPassword: "" });
  const m = useMutation({ mutationFn: () => authApi.changePassword(form), onSuccess: () => setForm({ currentPassword: "", newPassword: "" }) });
  return (
    <Card>
      <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); m.mutate(); }}>
        <h2 className="font-semibold">{t("settings.security.password")}</h2>
        {m.error && <Alert tone="danger">{toMsg(m.error)}</Alert>}
        {m.isSuccess && <Alert tone="success">{t("settings.security.passwordChanged")}</Alert>}
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t("settings.security.currentPassword")}>
            <Input type="password" dir="ltr" required autoComplete="current-password" value={form.currentPassword} onChange={(e) => setForm({ ...form, currentPassword: e.target.value })} />
          </Field>
          <Field label={t("settings.security.newPassword")} hint={t("common.passwordHint")}>
            <Input type="password" dir="ltr" required minLength={12} autoComplete="new-password" value={form.newPassword} onChange={(e) => setForm({ ...form, newPassword: e.target.value })} />
          </Field>
        </div>
        <Button type="submit" loading={m.isPending}>{t("common.save")}</Button>
      </form>
    </Card>
  );
}

function TotpCard({ user }: { user: PublicUser }) {
  const { t } = useTranslation();
  const toMsg = useApiError();
  const qc = useQueryClient();
  const [enrol, setEnrol] = useState<{ secret: string; otpauthUrl: string; qr: string } | null>(null);
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [disabling, setDisabling] = useState(false);
  const refresh = () => qc.invalidateQueries({ queryKey: authStatusQuery.queryKey });

  const begin = useMutation({
    mutationFn: authApi.totpBegin,
    onSuccess: async (r) => setEnrol({ ...r, qr: await QRCode.toDataURL(r.otpauthUrl, { margin: 1, width: 192 }) }),
  });
  const confirm = useMutation({ mutationFn: () => authApi.totpConfirm(code), onSuccess: () => { setEnrol(null); setCode(""); void refresh(); } });
  const disable = useMutation({ mutationFn: () => authApi.totpDisable(password, code), onSuccess: () => { setDisabling(false); setCode(""); setPassword(""); void refresh(); } });
  useEffect(() => () => setEnrol(null), []);

  return (
    <Card className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="font-semibold">{t("settings.security.totp")}</h2>
          <p className={user.totpEnabled ? "text-sm text-success" : "text-sm text-fg-muted"}>{user.totpEnabled ? t("settings.security.totpOn") : t("settings.security.totpOff")}</p>
        </div>
        {!user.totpEnabled && !enrol && <Button onClick={() => begin.mutate()} loading={begin.isPending}>{t("settings.security.totpEnable")}</Button>}
        {user.totpEnabled && !disabling && <Button variant="danger" onClick={() => setDisabling(true)}>{t("settings.security.totpDisable")}</Button>}
      </div>
      {confirm.isSuccess && <Alert tone="success">{t("settings.security.totpEnabled")}</Alert>}
      {disable.isSuccess && <Alert tone="success">{t("settings.security.totpDisabled")}</Alert>}
      {(begin.error || confirm.error || disable.error) && <Alert tone="danger">{toMsg(begin.error ?? confirm.error ?? disable.error)}</Alert>}

      {enrol && (
        <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); confirm.mutate(); }}>
          <p className="text-sm text-fg-muted">{t("settings.security.totpScan")}</p>
          <div className="flex flex-col items-start gap-4 sm:flex-row">
            <img src={enrol.qr} alt="QR" className="size-48 rounded-md border border-border bg-white p-2" />
            <div className="space-y-2 text-sm">
              <p className="text-fg-muted">{t("settings.security.totpManual")}</p>
              <code className="block rounded bg-surface-2 px-2 py-1 font-mono text-xs break-all">{enrol.secret}</code>
            </div>
          </div>
          <Field label={t("login.totpCode")}>
            <CodeInput required value={code} onChange={setCode} className="max-w-xs" />
          </Field>
          <div className="flex gap-2">
            <Button type="submit" loading={confirm.isPending} disabled={code.length !== 6}>{t("settings.security.totpConfirm")}</Button>
            <Button type="button" variant="ghost" onClick={() => { setEnrol(null); setCode(""); }}>{t("common.cancel")}</Button>
          </div>
        </form>
      )}

      {disabling && (
        <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); disable.mutate(); }}>
          <p className="text-sm text-fg-muted">{t("settings.security.totpDisableHint")}</p>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t("common.password")}>
              <Input type="password" dir="ltr" required autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
            </Field>
            <Field label={t("login.totpCode")}>
              <CodeInput required value={code} onChange={setCode} />
            </Field>
          </div>
          <div className="flex gap-2">
            <Button type="submit" variant="danger" loading={disable.isPending} disabled={code.length !== 6}>{t("settings.security.totpDisable")}</Button>
            <Button type="button" variant="ghost" onClick={() => { setDisabling(false); setCode(""); setPassword(""); }}>{t("common.cancel")}</Button>
          </div>
        </form>
      )}
    </Card>
  );
}

function SessionsCard() {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["auth", "sessions"], queryFn: authApi.sessions });
  const { confirm } = useConfirm();
  const revoke = useMutation({ mutationFn: authApi.revokeSession, onSuccess: () => qc.invalidateQueries({ queryKey: ["auth", "sessions"] }) });
  const askRevokeOthers = async () => { if (await confirm({ title: t("confirm.revokeSessionsTitle"), message: t("confirm.revokeSessionsMsg"), confirmLabel: t("settings.security.revokeOthers"), danger: true })) for (const s of others) await revoke.mutateAsync(s.id); };
  const fmt = (iso: string) => new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium", timeStyle: "short" }).format(new Date(iso));
  const others = (q.data ?? []).filter((s) => !s.current);
  return (
    <Card className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="font-semibold">{t("settings.security.sessions")}</h2>
        {others.length > 0 && (
          <Button variant="secondary" size="sm" loading={revoke.isPending} onClick={() => void askRevokeOthers()}>
            {t("settings.security.revokeOthers")}
          </Button>
        )}
      </div>
      <ul className="divide-y divide-border">
        {(q.data ?? []).map((s) => (
          <li key={s.id} className="flex items-center justify-between gap-3 py-2 text-sm">
            <div className="min-w-0">
              <div className="truncate" dir="ltr">
                {s.userAgent ?? t("common.unknown")} {s.ip && <span className="text-fg-muted">· {s.ip}</span>}
              </div>
              <div className="text-xs text-fg-muted">
                {s.current ? <span className="text-success">{t("settings.security.current")}</span> : `${t("settings.security.lastSeen")}: ${fmt(s.lastSeenAt)}`}
              </div>
            </div>
            {!s.current && <Button variant="ghost" size="sm" onClick={() => revoke.mutate(s.id)}>{t("settings.security.revoke")}</Button>}
          </li>
        ))}
      </ul>
    </Card>
  );
}
